import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import LoaderIndex from "~/containers/LoaderIndex";
import Volby2026Nav from "~/components/elections/volby2026/Volby2026Nav";
import ElectionProgressBar from "~/components/elections/volby2026/ElectionProgressBar";
import ElectionStats from "~/components/elections/volby2026/ElectionStats";
import ElectionStatsTable from "~/components/elections/volby2026/ElectionStatsTable";
import RegionChairCard from "~/components/elections/volby2026/RegionChairCard";
import MunicipalitySearch from "~/components/elections/volby2026/MunicipalitySearch";
import CandidateBarList from "~/components/elections/volby2026/CandidateBarList";
import { BASE_URL, formatNumber, formatPercent, statusLabel } from "~/utility/volby2026";

const paginationComponentOptions = {
  rowsPerPageText: "Výsledkov na stranu",
  rangeSeparatorText: "z",
  selectAllRowsItem: true,
  selectAllRowsItemText: "Zobraziť všetko",
};

const Volby2026OsoPage = (props: { legend: any; results: any }) => {
  const legend = props.legend || {};
  const results = props.results || {};
  const municipalities = legend.municipalities || [];
  const summary = results.summary || {};

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [obecData, setObecData] = useState<any>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchObec = (code: string) => {
    axios
      .get(`${BASE_URL}/obec/${code}.json`)
      .then((res) => setObecData(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!selectedCode) {
      setObecData(null);
      return;
    }
    fetchObec(selectedCode);
    pollRef.current = setInterval(() => fetchObec(selectedCode), 60000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCode]);

  const handleSelect = (code: string, label: string) => {
    setSelectedCode(code);
    setSelectedLabel(label);
  };

  const handleClear = () => {
    setSelectedCode(null);
    setSelectedLabel(null);
  };

  const mayorCandidates = obecData?.mayor_candidates || null;
  const mayor = obecData?.mayor || null;
  const obecStats = obecData?.stats || null;
  const electionType = obecData?.municipality?.election_type;
  const hasMayorElection = electionType === 1 || electionType === 2;
  const mayorFinal = !!obecData?.final;

  const columnsMayorCandidates = [
    { name: "Poradie na hlasovacom lístku", selector: (row: any) => row.ballot_no, sortable: true },
    { name: "Meno a priezvisko", selector: (row: any) => row.name, sortable: true },
    { name: "Politický subjekt", selector: (row: any) => row.parties, sortable: true },
    { name: "Počet platných hlasov", selector: (row: any) => row.votes, sortable: true },
    { name: "Podiel platných hlasov v %", selector: (row: any) => formatPercent(row.votes_pct), sortable: true },
    { name: "Poznámka", selector: (row: any) => statusLabel(row.status, !!obecData?.final), sortable: true },
  ];

  return (
    <div>
      <div className="container static-page elections">
        <h1>VOĽBY DO ORGÁNOV SAMOSPRÁVY OBCÍ 2026</h1>

        <Volby2026Nav active="oso" />

        {!!summary.protocols_total && (
          <ElectionProgressBar
            processed={summary.protocols_processed}
            total={summary.protocols_total}
            processedPct={summary.protocols_processed_pct}
          />
        )}

        <ElectionStats
          items={[
            { value: formatPercent(summary.turnout_pct), label: "účasť", highlight: true },
            { value: formatNumber(summary.voters_participated), label: "zúčastnených" },
            {
              value: formatPercent(summary.envelopes_cast_pct),
              label: "voličov, ktorí odovzdali obálku podľa § 24 (osobne)",
              highlight: true,
            },
            {
              value: formatNumber(summary.envelopes_cast),
              label: "voličov, ktorí odovzdali obálku podľa § 24 (osobne)",
            },
            { value: formatNumber(summary.voters_registered), label: "voličov zapísaných v zoznamoch voličov" },
            { value: formatNumber(summary.valid_ballots_mayor), label: "platných hlasov spolu" },
          ]}
        />

        <MunicipalitySearch
          municipalities={municipalities}
          selectedLabel={selectedLabel}
          onSelect={handleSelect}
          onClear={handleClear}
        />

        {selectedCode && (
          <>
            {!hasMayorElection && (
              <div className="elections-section">
                <p className="temp-text">Táto obec nevolí starostu/primátora.</p>
              </div>
            )}

            {hasMayorElection && !obecData?.has_results && (
              <div className="elections-section">
                <p className="temp-text">Zatiaľ žiadne výsledky pre túto obec.</p>
              </div>
            )}

            {hasMayorElection && obecData?.has_results && (
              <div className="elections-section">
                <h2>{mayorFinal ? "ZVOLENÝ STAROSTA" : "PRIEBEŽNE VEDIE"}</h2>

                <div className="v26-chair-grid single">
                  <RegionChairCard regionName={selectedLabel || ""} chair={mayor} />
                </div>

                {obecStats && (
                  <ElectionStatsTable
                    items={[
                      { value: formatPercent(obecStats.turnout_pct), label: "účasť voličov", highlight: true },
                      { value: formatNumber(obecStats.voters_registered), label: "zapísaných voličov" },
                      { value: formatNumber(obecStats.envelopes_cast), label: "odovzdaných obálok" },
                      {
                        value: formatNumber(obecStats.valid_ballots_mayor),
                        label: "platných hlasovacích lístkov odovzdaných pre voľby starostu",
                      },
                    ]}
                  />
                )}

                {mayorCandidates && (
                  <>
                    <h2>PLATNÉ HLASY PRE KANDIDÁTOV NA STAROSTU</h2>
                    <CandidateBarList candidates={mayorCandidates} />
                    <h2>POČET A PODIEL PLATNÝCH HLASOV ODOVZDANÝCH PRE KANDIDÁTOV NA STAROSTU</h2>
                    <div className="data-table-wrapper">
                      <DataTable
                        columns={columnsMayorCandidates}
                        data={mayorCandidates}
                        pagination
                        paginationComponentOptions={paginationComponentOptions}
                        noDataComponent="Žiadne výsledky"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <LoaderIndex />
    </div>
  );
};

export default Volby2026OsoPage;
